/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useState } from "react";
import { htmlToPlainText, sanitizeHtml } from "../../../utils/sanitize";
import styled from "styled-components";
import BookingStepItem from "../steps/BookingStepItem";
import Text from "../../ui/Text";
import Title from "../../ui/Title";
import Flex from "../../ui/Flex";
import Stack from "../../ui/Stack";
import { ROUTES } from "../../../constants/constants";
import { useStepManager } from "../../../hooks/useStepManager";
import Icon from "../../ui/Icon";
import Anchor from "../../ui/Anchor";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalImage, ModalTitle } from "../../ui/Modal";
import { useAssetsSrc } from "../../../hooks/useAssetsSrc";
import { useBooking } from "../../../hooks/useBooking";

const StyledLocationItem = styled((props) => <BookingStepItem {...props} />)`
  padding: 12px;
`;

const LocationItemInner = styled(Flex)`
    ${({ theme }) => theme.containerMedia.sm} {
      align-items: start;
    }
`;

const LocationItemMedia = styled.div`
  position: relative;
  display: block;
  place-items: center;
  place-content: center;
  width: 150px;
  height: 150px;
  border-radius: 0.5rem;
  flex-shrink: 0;
  overflow: hidden;

  ${({ theme }) => theme.containerMedia.sm} {
    width: 90px;
    height: 90px;
  }
`;

const LocationItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;


const LocationItemInfoList = styled.ul`
  && {
    display: grid;
    gap: 0.125rem;
    margin-top: 8px;
  }
`;

const LocationItemInfoListItemIcon = styled(Flex)`
  flex-shrink: 0;
`;

const LocationItem = ({ location }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setSelectedLocation, selectedProvider, setSelectedProvider, resetSelectedDate, setSelectedServices } =
    useBooking();
  const { navigateToStep } = useStepManager();
  const locationImage = useAssetsSrc(location.picture_preview);

  const handleLocationSelect = () => {
    setSelectedLocation(location);
    setSelectedServices([]);

    if (location.providers.find((provider) => provider.id !== selectedProvider?.id)) {
      setSelectedProvider(null);
      resetSelectedDate();
    }

    return navigateToStep(ROUTES.HOME);
  };

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  return (
    <>
      <StyledLocationItem onClick={handleLocationSelect}>
        <LocationItemInner $align="center" $gap="1rem">
          <LocationItemMedia>
            {!locationImage && <Icon.LocationPlaceholderIcon size={64} strokeWidth={3} color="iconStroke" />}
            {locationImage && <LocationItemImage src={locationImage} alt={location.name} />}
          </LocationItemMedia>
          <Stack $gap="0.125rem" $align="start">
            <Title size="h3">{location.name}</Title>

            {location.description && (
              <Text size="small" lineClamp={2} onReadMoreClick={openModal}>
                {htmlToPlainText(location.description)}
              </Text>
            )}

            {(location.address1 || location.address2 || location.phone) && (
              <LocationItemInfoList>
                {(location.address1 || location.address2) && (
                  <Flex as="li" $align="center" $gap="0.25rem">
                    <LocationItemInfoListItemIcon>
                      <Icon.Location />
                    </LocationItemInfoListItemIcon>
                    <Text
                      as={Anchor}
                      color="dark"
                      size="small"
                      to={`https://www.google.com/maps/search/?api=1&query=${location.full_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {location.full_address}
                    </Text>
                  </Flex>
                )}
                {location.phone && (
                  <Flex as="li" $align="center" $gap="0.25rem">
                    <LocationItemInfoListItemIcon>
                      <Icon.Phone />
                    </LocationItemInfoListItemIcon>
                    <Text as={Anchor} color="dark" size="small" to={`tel:${location.phone}`} rel="noopener noreferrer">
                      {location.phone}
                    </Text>
                  </Flex>
                )}
              </LocationItemInfoList>
            )}
          </Stack>
        </LocationItemInner>
      </StyledLocationItem>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <ModalContent>
          {locationImage ? (
            <>
              <ModalCloseButton />
              <ModalImage src={locationImage} alt={location.name} />
            </>
          ) : (
            <ModalHeader>
              <ModalTitle>{location.name}</ModalTitle>
            </ModalHeader>
          )}
          <ModalBody>
            {locationImage && <ModalTitle>{location.name}</ModalTitle>}
            <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(location.description) }} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default LocationItem;
